import React from 'react';
import { HoverElement } from '../../types';

interface EdgeData {
  id: string;
  from: string;
  to: string;
  current?: number;
  rating: number;
}

interface NodeData {
  id: string;
  x: number;
  y: number;
}

interface DragState {
  isDragging: boolean;
  edgeId: string | null;
  dragType: 'position' | 'value' | 'rating' | 'pan';
}

interface EdgeProps {
  edge: EdgeData;
  fromNode: NodeData;
  toNode: NodeData;
  onMouseDown: (event: React.MouseEvent<SVGGElement>, edgeId: string) => void;
  onContextMenu: (event: React.MouseEvent<SVGGElement>, edgeId: string) => void;
  dragState: DragState;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  // Transmission (congestion) rent collected by this line, in $/h. Undefined
  // when LMPs aren't available (unsolved/infeasible dispatch).
  rent?: number;
}

const getLineWidth = (current: number | undefined): number => {
  if (!current) return 1;
  return Math.max(1, Math.abs(current) * 0.2);
};

const getLineColor = (current: number | undefined, rating: number): string => {
  if (!current) return '#666';
  
  const currentMagnitude = Math.abs(current);
  const utilizationRatio = currentMagnitude / rating;
  
  // Normal operation (green to yellow): 0% to 80% of rating
  if (utilizationRatio <= 0.8) {
    const intensity = utilizationRatio / 0.8; // 0 to 1 over the normal range
    const green = 200;
    const red = Math.floor(intensity * 150); // 0 to 150
    return `rgb(${red}, ${green}, 0)`;
  }
  // Warning (yellow to orange): 80% to 100% of rating
  else if (utilizationRatio <= 1.0) {
    const intensity = (utilizationRatio - 0.8) / 0.2; // 0 to 1 over warning range
    const red = 150 + Math.floor(intensity * 105); // 150 to 255
    const green = 200 - Math.floor(intensity * 100); // 200 to 100
    return `rgb(${red}, ${green}, 0)`;
  }
  // Overloaded (red): above 100% of rating
  else {
    return '#FF0000'; // Bright red for overload
  }
};

// Arrow scaling factors
  const arrowScale = 0.8; // Controls overall arrow size relative to stroke width
  const arrowWidthScale = 1.0; // Controls arrow width relative to arrow size

export function Edge({
  edge,
  fromNode,
  toNode,
  onMouseDown,
  onContextMenu,
  dragState,
  hoverElement,
  onElementHover,
  rent,
}: EdgeProps) {
  // Only surface rent on lines that actually collect a meaningful amount, so
  // uncongested lines (rent ≈ $0) don't clutter the diagram.
  const showRent = rent !== undefined && rent >= 1;
  // Check if this edge is highlighted
  const isHighlighted = hoverElement?.type === 'Edge' && hoverElement?.id === edge.id;
  
  // Calculate line properties first
  const strokeColor = isHighlighted ? '#FFD700' : getLineColor(edge.current, edge.rating);
  const strokeWidth = getLineWidth(edge.current);
  
  // Calculate arrow positions and angles
  const dx = toNode.x - fromNode.x;
  const dy = toNode.y - fromNode.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const unitX = dx / length;
  const unitY = dy / length;
  
  // Arrow size - smaller and scale with line width
  const arrowSize = Math.max(4, strokeWidth * arrowScale);
  
  // Determine actual flow direction based on current value
  // Positive current means flow is in the direction from -> to
  // Negative current means flow is in the direction to -> from
  // This ensures arrow direction is independent of edge definition order
  const actualFlowDirection = !edge.current ? 0 : Math.sign(edge.current);
  const hasFlow = edge.current !== undefined && edge.current !== 0;
  
  // Node radius (matching Node component)
  const nodeRadius = 25;
  
  // Calculate arrow position and direction based on actual flow
  let arrowX: number, arrowY: number, arrowUnitX: number, arrowUnitY: number;
  
  if (actualFlowDirection >= 0) {
    // Flow goes from -> to (or no flow)
    arrowX = toNode.x - unitX * nodeRadius;
    arrowY = toNode.y - unitY * nodeRadius;
    arrowUnitX = unitX;
    arrowUnitY = unitY;
  } else {
    // Flow goes to -> from (negative current)
    arrowX = fromNode.x + unitX * nodeRadius;
    arrowY = fromNode.y + unitY * nodeRadius;
    arrowUnitX = -unitX;
    arrowUnitY = -unitY;
  }
  
  // Arrow points for triangle
  const perpX = -arrowUnitY;
  const perpY = arrowUnitX;

  return (
    <g 
      onContextMenu={(e) => onContextMenu(e, edge.id)}
      onMouseDown={(e) => onMouseDown(e, edge.id)}
      onMouseEnter={() => onElementHover({ type: 'Edge', id: edge.id })}
      onMouseLeave={() => onElementHover(null)}
      style={{ cursor: 'pointer' }}
    >
      <line
        x1={hasFlow && actualFlowDirection < 0 ? fromNode.x + unitX * (nodeRadius + arrowSize) : fromNode.x + unitX * nodeRadius}
        y1={hasFlow && actualFlowDirection < 0 ? fromNode.y + unitY * (nodeRadius + arrowSize) : fromNode.y + unitY * nodeRadius}
        x2={hasFlow && actualFlowDirection >= 0 ? toNode.x - unitX * (nodeRadius + arrowSize) : toNode.x - unitX * nodeRadius}
        y2={hasFlow && actualFlowDirection >= 0 ? toNode.y - unitY * (nodeRadius + arrowSize) : toNode.y - unitY * nodeRadius}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      
      {/* Arrow at destination end indicating actual flow direction */}
      {hasFlow && (
        <polygon
          points={`
            ${arrowX},${arrowY}
            ${arrowX - arrowUnitX * arrowSize + perpX * arrowSize * arrowWidthScale},${arrowY - arrowUnitY * arrowSize + perpY * arrowSize * arrowWidthScale}
            ${arrowX - arrowUnitX * arrowSize - perpX * arrowSize * arrowWidthScale},${arrowY - arrowUnitY * arrowSize - perpY * arrowSize * arrowWidthScale}
          `}
          fill={strokeColor}
        />
      )}
      {/* Rating adjustment indicator */}
      {dragState.isDragging && dragState.edgeId === edge.id && dragState.dragType === 'rating' && (
        <line
          x1={fromNode.x}
          y1={fromNode.y}
          x2={toNode.x}
          y2={toNode.y}
          stroke="#FF9800"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="8,4"
          opacity="0.6"
        />
      )}
      {/* Current and rating labels */}
      {hasFlow && (
        <>
          <text
            x={(fromNode.x + toNode.x) / 2}
            y={(fromNode.y + toNode.y) / 2 - 8}
            textAnchor="middle"
            fontSize="12"
            fill="#333"
            fontWeight="bold"
            pointerEvents="none"
          >
            {Math.abs(edge.current!).toFixed(1)}MW
          </text>
          <text
            x={(fromNode.x + toNode.x) / 2}
            y={(fromNode.y + toNode.y) / 2 + 8}
            textAnchor="middle"
            fontSize="10"
            fill="#666"
            pointerEvents="none"
          >
            {((Math.abs(edge.current!) / edge.rating) * 100).toFixed(0)}% of {edge.rating.toFixed(0)}MW
          </text>
          {showRent && (
            <text
              x={(fromNode.x + toNode.x) / 2}
              y={(fromNode.y + toNode.y) / 2 + 21}
              textAnchor="middle"
              fontSize="11"
              fill="#8E24AA"
              fontWeight="bold"
              pointerEvents="none"
            >
              {`$${Math.round(rent!).toLocaleString()}/h rent`}
            </text>
          )}
        </>
      )}
    </g>
  );
}
